import React, { Component } from "react";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import { paymentSlashSuffix } from "src/utils/dates";
import {
    FlexContainer, CardList, LottieLoadingIndicator,
    Toast,
    BusinessPortalHeader,
    PortalFormActions,
    PortalFormScrollBody,
    PortalFormSection,
    PortalFormSurface,
    PortalCaption,
    PAYMENT_FLOW_MAX_WIDTH,
    PortalPaymentOptionButton,
    PortalPaymentOptionGrid,
    PortalPaymentOptionIndicator,
    PortalPanelHeader,
    PortalSectionTitle,
    StandardModalActionButton,
    StandardModalActionGroup
} from "src/components";

import Price from "../components/Price";
import SubscriptionOverview from "../components/SubscriptionOverview";
import "@trendmicro/react-radio/dist/react-radio.css";
import { SubscriptionContext } from "../SubscriptionContext";
import { Forms } from "../Subscription";
import AirwallexForm, { AirwallexProduct } from "src/scenes/Subscription/components/AirwallexForm";
import { calculateTotals } from "src/utils/helpers";

export const PaymentSelectionButton = PortalPaymentOptionButton;

export const PaymentSelectionIcon = PortalPaymentOptionIndicator;

export const Methods = {
    "EXISTING": "Existing Card",
    "NEW": "New Card"
};

const Screens = {
    OVERVIEW: "Overview",
    PAYMENT: "Payment"
};

class Review extends Component {
    state = {
        loading: false,
        method: Methods.EXISTING,
        screen: Screens.OVERVIEW
    };

    static contextType = SubscriptionContext;

    /**
     *  Returns a Promise that is being used to `await` in CardList.js thisPurchaseClicked()
    */
    purchaseMore = (selectedMethod, methodSource) => {
        if (methodSource === "square") {
            return this.purchaseWithOurApi(
                `${API_URL}/company/square/more`,
                {
                    numOfUsers: this.context.moreUsers,
                    cardId: selectedMethod
                });
        } else if (methodSource === "airwallex") {
            return this.purchaseWithOurApi(
                `${API_URL}/company/airwallex/purchaseNow/moreUsers`,
                {
                    numberOfUsers: this.context.moreUsers,
                    paymentMethodId: selectedMethod
                });
        } else {
            console.log(`Bad method source: ${methodSource}`);
        }
    };

    purchaseWithOurApi = async (url, requestBody) => {
        const { token, nextClick } = this.context;
        this.setState({
            loading: true
        });
        axios.post(url, requestBody, {
            headers: {
                Authorization: "Bearer " + token
            }
        }).then(() => {
            this.setState({
                loading: false
            });
            nextClick();
        }).catch(error => {
            this.setState({
                loading: false
            });
            return Toast.error(error.getApiErrorDetails().message);
        });
    };

    updateMethod = (value) => {
        this.setState({
            method: value
        });
    };

    changeScreen = (screen) => {
        this.setState({ screen: screen });
    };

    render() {
        //add method and updateValue when using radio buttons back
        const { totalNumber, companyName, abn, website, paymentMode, currentSubscription,
            moreUsers, backToPrevious, email,
            currentForm, token, nextClick, setUserPermissions } = this.context;

        const { method, screen } = this.state;

        if (this.state.loading) {
            return <LottieLoadingIndicator />;
        }

        const updatePaymentAndPurchase = () => {
            nextClick();
        };

        if (!currentSubscription) {
            return (<>
                Your subscription could not be found.
            </>);
        }

        const centsPerPeriod = currentSubscription.periodNetAmountCents;

        const taxFraction = currentSubscription.taxFraction;

        const pricePerUserDisplay = centsPerPeriod / 100;
        const userCount = currentForm === Forms.REVIEW ? totalNumber : moreUsers;
        const periodsToPayFor = currentForm === Forms.REVIEW ?
            currentSubscription.periodsDue || 0 :
            (
                // Adding more users; for upfront payment must pay the remaining time for these users
                currentSubscription.paymentPeriod === 1 ?
                    1 :
                    currentSubscription.periodsRemaining || 0
            );
        const subTotalCents = centsPerPeriod * userCount * periodsToPayFor;

        const { subTotal, gst, processingFee, total: totalAmount } = calculateTotals({
            subtotalCents: subTotalCents,
            taxFraction: currentSubscription.taxFraction,
            processingRate: currentSubscription.processingFee
        });

        const perPeriodSlashSuffix = paymentSlashSuffix(1, currentSubscription.durationUnit);
        const perFullDurationSlashSuffix = paymentSlashSuffix(currentSubscription.paymentPeriod === null ? currentSubscription.durationNumber : 1, currentSubscription.durationUnit);

        const pricing = {
            plan: {
                name: currentSubscription.name,
                paymentDescription: currentSubscription.frequencyName
            },
            centsPerPeriod,
            taxFraction,
            pricePerUserDisplay,
            subTotal,
            gst,
            totalAmount,
            perPeriodSlashSuffix,
            perFullDurationSlashSuffix,
            currency: currentSubscription.currencyCode,
            processingFee: processingFee
        };

        let currentFormIsReview = false;
        let currentFormIsCheckout = false;
        switch (currentForm) {
            case Forms.REVIEW:
                currentFormIsReview = true;
                break;
            case Forms.CHECKOUT:
                currentFormIsCheckout = true;
                break;
            default:
                console.error(`Incorrect form specified: ${currentForm}`);
                Toast.error("A form error occurred.");
        }

        return (
            <React.Fragment>
                { screen === Screens.OVERVIEW ?
                    <SubscriptionOverview
                        totalNumber={userCount}
                        pricing={pricing}
                        goToPayment={() => this.changeScreen(Screens.PAYMENT)}
                    />
                    :
                    <React.Fragment>
                        <BusinessPortalHeader
                            title={currentFormIsReview ? "Subscription Payment: Step 5" : currentFormIsCheckout ? "Purchase More User Licenses" : ""}
                            description={currentFormIsReview ?
                                "Enter a payment method for your business subscription and select Pay Now to complete setup. Your payment method will be used for subscription renewals. You can always add a new payment method via your Account Settings."
                                : currentFormIsCheckout ? "Select an existing payment method or add a new payment method for this subscription." : ""
                            }
                            maxWidth={PAYMENT_FLOW_MAX_WIDTH}
                        />
                        <PortalFormSurface maxWidth={PAYMENT_FLOW_MAX_WIDTH} marginTop="0" flex="1 1 auto" minHeight="0">
                            <PortalFormScrollBody columns="repeat(2, minmax(0, 1fr))" padding="1.25rem" gap="1rem">
                                <PortalFormSection>
                                    <Price
                                        Forms={Forms}
                                        currentForm={currentForm}
                                        companyName={companyName}
                                        totalNumber={userCount}
                                        periodsForPayment={periodsToPayFor}
                                        currentSubscription={currentSubscription}
                                        goToOverview={() => this.changeScreen(Screens.OVERVIEW)}
                                        processingFee={processingFee}
                                        gst={gst}
                                        totalAmount={totalAmount}
                                        hideBackButton
                                    />
                                </PortalFormSection>
                                <PortalFormSection>
                                    <PortalPanelHeader withDivider>
                                        <div>
                                            <PortalSectionTitle>
                                                { currentFormIsReview && "Confirm Payment" }
                                                { currentFormIsCheckout && "Payment Method" }
                                            </PortalSectionTitle>
                                            <PortalCaption margin="0.25rem 0 0">
                                                { currentFormIsReview && "Enter a payment method to complete setup." }
                                                { currentFormIsCheckout && "Select an existing payment method or add a new card." }
                                            </PortalCaption>
                                        </div>
                                    </PortalPanelHeader>
                                    { currentFormIsReview &&
                                        <FlexContainer direction="column" flexGrow="1">
                                            <AirwallexForm
                                                token={token} email={email}
                                                productType={AirwallexProduct.INITIAL}
                                                companyName={companyName}
                                                abn={abn}
                                                website={website}
                                                numberOfUsers={totalNumber}
                                                paymentMode={paymentMode}
                                                setUserPermissions={setUserPermissions}
                                                backToPrevious={backToPrevious}
                                                nextClick={nextClick} />
                                        </FlexContainer>
                                    }
                                    { currentFormIsCheckout &&
                                        <FlexContainer direction="column" margin="1rem 0 0">
                                            <PortalPaymentOptionGrid>
                                                <PaymentSelectionButton
                                                    $active={method === Methods.EXISTING}
                                                    onClick={() => this.updateMethod(Methods.EXISTING)}
                                                >
                                                    <PaymentSelectionIcon
                                                        $active={method === Methods.EXISTING}
                                                    />
                                                    Use an existing card
                                                </PaymentSelectionButton>
                                                <PaymentSelectionButton
                                                    $active={method === Methods.NEW}
                                                    onClick={() => this.updateMethod(Methods.NEW)}
                                                >
                                                    <PaymentSelectionIcon
                                                        $active={method === Methods.NEW}
                                                    />
                                                    Add a new card
                                                </PaymentSelectionButton>
                                            </PortalPaymentOptionGrid>
                                            { this.state.method === Methods.NEW ?
                                                <FlexContainer align="start" direction="column" flexGrow="1">
                                                    <AirwallexForm
                                                        token={token}
                                                        email={email}
                                                        productType={AirwallexProduct.ADD_USERS}
                                                        companyName={companyName}
                                                        numberOfUsers={moreUsers}
                                                        backToPrevious={backToPrevious}
                                                        nextClick={updatePaymentAndPurchase} />
                                                </FlexContainer>
                                                :
                                                <CardList
                                                    token={token}
                                                    onBackClicked={() => backToPrevious()}
                                                    onPurchaseClicked={this.purchaseMore}
                                                    onDeleteClicked={null}
                                                    hideBackButton
                                                    minHeight="auto"
                                                />
                                            }
                                        </FlexContainer>
                                    }
                                </PortalFormSection>
                            </PortalFormScrollBody>
                            <PortalFormActions sticky={false}>
                                <StandardModalActionGroup $pushRight>
                                    <StandardModalActionButton type="button" onClick={() => this.changeScreen(Screens.OVERVIEW)}>
                                        Back
                                    </StandardModalActionButton>
                                </StandardModalActionGroup>
                            </PortalFormActions>
                        </PortalFormSurface>
                    </React.Fragment>
                }
            </React.Fragment>
        );
    }
}

export default Review;
